import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  doc,
  getDoc,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { CategoryKey, NewsItem } from '@/types';

/* ======================================================
   Cache Layer (memory + localStorage)
====================================================== */

type CacheEntry<T> = { data: T; ts: number };
const memoryCache = new Map<string, CacheEntry<any>>();
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

function isPastDate(dateKey: string) {
  return dateKey < getTodayKey();
}

function newsCacheKey(language: string, category: string, dateKey: string) {
  return `news:${language}:${category}:${dateKey}`;
}

function cityNewsCacheKey(
  country: string,
  state: string,
  city: string,
  language: string,
  category: string,
  dateKey: string
) {
  return `citynews:${country}:${state}:${city}:${language}:${category}:${dateKey}`;
}

function cityDatesCacheKey(
  country: string,
  state: string,
  city: string,
  language: string,
  indexName: string
) {
  return `citydates:${country}:${state}:${city}:${language}:${indexName}`;
}

function cityCategoryCacheKey(
  country: string,
  state: string,
  city: string,
  language: string,
  categoryKey: string
) {
  return `citycat:${country}:${state}:${city}:${language}:${categoryKey}`;
}

function getCache<T>(key: string, forever = false): T | null {
  const now = Date.now();

  const mem = memoryCache.get(key);
  if (mem) {
    if (forever) return mem.data;
    if (now - mem.ts < CACHE_TTL) return mem.data;
  }

  const raw = localStorage.getItem(key);
  if (!raw) return null;

  const parsed: CacheEntry<T> = JSON.parse(raw);

  if (!forever && now - parsed.ts > CACHE_TTL) {
    localStorage.removeItem(key);
    return null;
  }

  memoryCache.set(key, parsed);
  return parsed.data;
}

function setCache<T>(key: string, data: T) {
  const entry: CacheEntry<T> = { data, ts: Date.now() };
  memoryCache.set(key, entry);
  localStorage.setItem(key, JSON.stringify(entry));
}

/* ======================================================
   Types
====================================================== */

export type GetNewsParams = {
  category: string;
  language: string;
  dateKey: string;
  pageSize?: number;
  cursor?: QueryDocumentSnapshot;
};

export type GetNewsResult = {
  items: NewsItem[];
  cursor?: QueryDocumentSnapshot;
  hasMore: boolean;
};

/* ======================================================
   Core News
====================================================== */

export async function getNews(params: GetNewsParams): Promise<GetNewsResult> {
  const { category, language, dateKey } = params;

  const itemsCol = collection(
    db,
    'news',
    language,
    category,
    dateKey,
    'items'
  );

  try {
    const snap = await getDocs(itemsCol);

    const items: NewsItem[] = snap.docs.map(d => ({
      id: d.id,
      ...(d.data() as any),
    }));

    return { items, hasMore: false };
  } catch (err) {
    console.error('Error fetching news:', err);
    return { items: [], hasMore: false };
  }
}

/* ======================================================
   Today News
====================================================== */

export async function getTodayNews(
  params: Omit<GetNewsParams, 'dateKey'>
): Promise<NewsItem[]> {

  const todayKey = getTodayKey();
  const cacheKey = newsCacheKey(
    params.language,
    params.category,
    todayKey
  );

  const cached = getCache<NewsItem[]>(cacheKey, false);
  if (cached) return cached;

  const res = await getNews({ ...params, dateKey: todayKey });

  setCache(cacheKey, res.items);
  return res.items;
}

/* ======================================================
   Older News
====================================================== */

export async function getOlderNews(
  params: Omit<GetNewsParams, 'dateKey'> & { dateKey: string }
): Promise<NewsItem[]> {

  const { language, category, dateKey } = params;

  const cacheKey = newsCacheKey(language, category, dateKey);

  const cached = getCache<NewsItem[]>(cacheKey, true);
  if (cached) return cached;

  const res = await getNews(params);

  setCache(cacheKey, res.items);
  return res.items;
}

/* ======================================================
   News Dates
====================================================== */

export async function getNewsDates(params: {
  category: string;
  language: string;
}): Promise<string[]> {

  const { category, language } = params;

  const dateIndexRef = doc(
    db,
    'news',
    language,
    category,
    'dateIndex'
  );

  const snap = await getDoc(dateIndexRef);

  if (!snap.exists()) return [];

  const data = snap.data();
  const dates = Array.isArray(data.dates) ? data.dates : [];

  return dates.sort((a, b) => (a < b ? 1 : -1));
}

/* ======================================================
   City News By Date (CACHED)
====================================================== */

export async function getCityNewsByDate(params: {
  country: string;
  state: string;
  city: string;
  language: string;
  dateKey: string;
  category: string;
}): Promise<NewsItem[]> {

  const { country, state, city, language, dateKey, category } = params;

  const cacheKey = cityNewsCacheKey(
    country,
    state,
    city,
    language,
    category,
    dateKey
  );

  const cached = getCache<NewsItem[]>(cacheKey, isPastDate(dateKey));
  if (cached) return cached;

  try {
    const itemsCol = collection(
      db,
      'CityRoutinePosts',
      country,
      state,
      city,
      language,
      category,
      dateKey
    );

    const q = query(
      itemsCol,
      orderBy('publishedAt', 'desc'),
      limit(40)
    );

    const snap = await getDocs(q);

    const items = snap.docs.map(d => ({
      id: d.id,
      ...(d.data() as any),
    }));

    setCache(cacheKey, items);

    return items;

  } catch (err) {
    console.error('Error fetching city news:', err);
    return [];
  }
}

/* ======================================================
   City News Dates (FOREVER CACHE)
====================================================== */

export async function getCityNewsDates(params: {
  country: string;
  state: string;
  city: string;
  language: string;
  indexName: string;
}): Promise<string[]> {

  const { country, state, city, language, indexName } = params;

  const cacheKey = cityDatesCacheKey(
    country,
    state,
    city,
    language,
    indexName
  );

  const cached = getCache<string[]>(cacheKey, true);
  if (cached) return cached;

  const ref = doc(
    db,
    'CityRoutinePosts',
    country,
    state,
    city,
    language,
    indexName
  );

  const snap = await getDoc(ref);
  if (!snap.exists()) return [];

  const dates = snap.data().dates ?? [];
  const sorted = dates.sort((a: string, b: string) => (a < b ? 1 : -1));

  setCache(cacheKey, sorted);

  return sorted;
}

/* ======================================================
   City Category News (CACHED)
====================================================== */

export async function getCityCategoryNews(params: {
  country: string;
  state: string;
  city: string;
  language: string;
  categoryKey: string;
  pageSize?: number;
}): Promise<NewsItem[]> {

  const {
    country,
    state,
    city,
    language,
    categoryKey,
    pageSize = 40
  } = params;

  const cacheKey = cityCategoryCacheKey(
    country,
    state,
    city,
    language,
    categoryKey
  );

  const cached = getCache<NewsItem[]>(cacheKey, false);
  if (cached) return cached;

  try {
    const path = `CityRoutinePosts/${country}/${state}/${city}/${language}/${categoryKey}/posts`;

    const postsCol = collection(db, path);

    const q = query(
      postsCol,
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    const snap = await getDocs(q);

    const items: NewsItem[] = snap.docs.map(doc => {
      const d = doc.data() as Record<string, any>;
      return {
        id: doc.id,
        title: typeof d.title === 'string' ? d.title : '',
        description: typeof d.description === 'string' ? d.description : undefined,
        url: typeof d.url === 'string' ? d.url : '',
        imageUrl: typeof d.imageUrl === 'string' ? d.imageUrl : undefined,
        videoFilePath: typeof d.videoFilePath === 'string' ? d.videoFilePath : undefined,
        publishedAt: typeof d.publishedAt === 'number' ? d.publishedAt : Date.now(),
        category: (d.category as CategoryKey) || '25',
        language: typeof d.language === 'string' ? d.language : 'en',
        city: typeof d.city === 'string' ? d.city : undefined,
        state: typeof d.state === 'string' ? d.state : undefined,
        country: typeof d.country === 'string' ? d.country : undefined,
        source: typeof d.source === 'string' ? d.source : undefined,
      };
    });

    setCache(cacheKey, items);

    return items;

  } catch (err) {
    console.error('Error fetching city category news:', err);
    return [];
  }
}
